import React from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/Label'
import { Key, CheckCircle, XCircle } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Field from '@/components/ui/Field'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'
import useUpdatePassword from '@/services/api/auth/useUpdatePassword'
import { PasswordInput } from '@/components/PasswordInput'

function RequirementItem({
  met,
  children,
}: {
  met: boolean
  children: React.ReactNode
}) {
  return (
    <li className="flex items-center gap-2 text-sm">
      {met ? (
        <CheckCircle className="h-4 w-4 text-green-600" />
      ) : (
        <XCircle className="h-4 w-4 text-red-400" />
      )}
      <span className={met ? 'text-green-700' : 'text-gray-600'}>
        {children}
      </span>
    </li>
  )
}

export default function PasswordSection() {
  const minLength = {
    label: 'At least 8 characters',
    test: (pwd: string) => pwd.length >= 8,
  }

  // Server rule: at least three of these four (PasswordCharacterClasses).
  const characterClasses = [
    {
      label: 'Uppercase letter',
      test: (pwd: string) => /\p{Lu}/u.test(pwd),
    },
    {
      label: 'Lowercase letter',
      test: (pwd: string) => /\p{Ll}/u.test(pwd),
    },
    { label: 'Number', test: (pwd: string) => /\p{N}/u.test(pwd) },
    {
      label: 'Special character',
      test: (pwd: string) => /[^\p{L}\p{N}]/u.test(pwd),
    },
  ]

  const classCount = (password: string) =>
    characterClasses.filter((req) => req.test(password)).length

  const isPasswordValid = (password: string) =>
    minLength.test(password) && classCount(password) >= 3

  const schema = z
    .object({
      currentPassword: z.string().min(1, 'Current password is required'),
      newPassword: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .refine(
          isPasswordValid,
          'Password does not meet security requirements',
        ),
      confirmPassword: z.string().min(1, 'Please confirm your password'),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    })
    .refine((data) => data.currentPassword !== data.newPassword, {
      message: 'New password must be different from current password',
      path: ['newPassword'],
    })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const { setError, formState, handleSubmit, reset, watch } = form
  const { errors } = formState
  const newPassword = watch('newPassword')

  const { action: updatePassword, status: updatePasswordStatus } =
    useUpdatePassword({
      setError,
      clientServerFieldMapping: {
        currentPassword: 'current_password',
        newPassword: 'password',
      },
    })

  const handlePasswordChange = async (values: any) => {
    await updatePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    })
    reset()
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-2">Change Password</h2>
        <p className="mb-4 text-gray-600">
          Update your password to keep your account secure.
        </p>
      </div>

      <form onSubmit={handleSubmit(handlePasswordChange)} className="space-y-6">
        <Field
          statusText={errors?.currentPassword?.message?.toString()}
          status={errors?.currentPassword ? 'error' : null}
        >
          <Label htmlFor="currentPassword">Current Password</Label>
          <PasswordInput
            id="currentPassword"
            {...form.register('currentPassword')}
            placeholder="Enter your current password"
          />
          <FieldStatusMessage />
        </Field>

        <Field
          statusText={errors?.newPassword?.message?.toString()}
          status={errors?.newPassword ? 'error' : null}
        >
          <Label htmlFor="newPassword">New Password</Label>
          <PasswordInput
            id="newPassword"
            {...form.register('newPassword')}
            placeholder="Enter your new password"
          />
          <FieldStatusMessage />
        </Field>

        <Field
          statusText={errors?.confirmPassword?.message?.toString()}
          status={errors?.confirmPassword ? 'error' : null}
        >
          <Label htmlFor="confirmPassword">Confirm New Password</Label>
          <PasswordInput
            id="confirmPassword"
            {...form.register('confirmPassword')}
            placeholder="Confirm your new password"
          />
          <FieldStatusMessage />
        </Field>

        {newPassword && (
          <div className="rounded-lg bg-gray-50 p-4">
            <h4 className="mb-2 flex items-center gap-2">
              <Key className="h-4 w-4" />
              Password Requirements
            </h4>
            <ul className="space-y-1">
              {[
                { ...minLength, met: minLength.test(newPassword) },
                {
                  label: 'At least 3 of the following:',
                  met: classCount(newPassword) >= 3,
                },
              ].map((req) => (
                <RequirementItem key={req.label} met={req.met}>
                  {req.label}
                </RequirementItem>
              ))}
            </ul>
            <ul className="ml-6 mt-1 space-y-1">
              {characterClasses.map((req) => (
                <RequirementItem key={req.label} met={req.test(newPassword)}>
                  {req.label}
                </RequirementItem>
              ))}
            </ul>
          </div>
        )}

        <Button
          type="submit"
          disabled={updatePasswordStatus.status === 'pending'}
        >
          {updatePasswordStatus.status === 'pending'
            ? 'Updating Password...'
            : 'Update Password'}
        </Button>
      </form>
    </div>
  )
}
